/**
 * Shopify embedded app billing tiers — separate from main-site Stripe plans.
 * Catalog synced from scripts/shopify-plans-catalog.source.json via sync-shopify-plans-catalog.cjs
 */

import {
  SHOPIFY_FREE_MAX_VIDEO_DURATION_SEC,
  SHOPIFY_PROFESSIONAL_MAX_VIDEO_DURATION_SEC,
  SHOPIFY_STUDIO_MAX_VIDEO_DURATION_SEC,
  LEGACY_SHOPIFY_PLAN_IDS,
  SHOPIFY_PLANS,
} from './shopifyPlansCatalog.generated';

export {
  SHOPIFY_FREE_MAX_VIDEO_DURATION_SEC,
  SHOPIFY_PROFESSIONAL_MAX_VIDEO_DURATION_SEC,
  SHOPIFY_STUDIO_MAX_VIDEO_DURATION_SEC,
  LEGACY_SHOPIFY_PLAN_IDS,
  SHOPIFY_PLANS,
};

export function canonicalShopifyPlanId(plan) {
  const raw = String(plan ?? '').trim().toLowerCase().replace(/\s+/g, '_');
  if (!raw) return 'shopify_free';
  if (LEGACY_SHOPIFY_PLAN_IDS[raw]) return LEGACY_SHOPIFY_PLAN_IDS[raw];
  if (raw.startsWith('shopify_')) return raw;
  if (raw === 'starter' || raw.includes('starter')) return 'shopify_professional';
  if (raw === 'professional' || raw.includes('professional')) return 'shopify_professional';
  if (raw === 'growth' || raw.includes('growth')) return 'shopify_studio';
  if (raw === 'studio' || raw.includes('studio')) return 'shopify_studio';
  if (raw === 'agency' || raw.includes('agency')) return 'shopify_agency';
  if (raw === 'unlimited' || raw.includes('unlimited')) return 'shopify_agency';
  return 'shopify_free';
}

export function isShopifyPlan(plan) {
  return String(plan ?? '').startsWith('shopify_');
}

export function isShopifyBillingProvider(billing) {
  return String(billing?.billingProvider ?? '') === 'shopify';
}

export function resolveShopifyPlanId(plan, billingProvider) {
  const normalized = normalizeShopifyPlanKey(plan);
  if (isShopifyPlan(normalized)) return canonicalShopifyPlanId(normalized);
  if (isShopifyBillingProvider({ billingProvider }) || String(plan ?? '') === 'free') {
    return 'shopify_free';
  }
  return 'shopify_free';
}

function normalizeShopifyPlanKey(plan) {
  const p = String(plan ?? '').toLowerCase().trim().replace(/\s+/g, '_');
  if (p.startsWith('shopify_')) return p;
  if (p === 'starter' || p.includes('starter')) return 'shopify_professional';
  if (p === 'professional' || p.includes('professional')) return 'shopify_professional';
  if (p === 'growth' || p.includes('growth')) return 'shopify_studio';
  if (p === 'studio' || p.includes('studio')) return 'shopify_studio';
  if (p === 'agency' || p.includes('agency')) return 'shopify_agency';
  if (p === 'unlimited' || p.includes('unlimited')) return 'shopify_agency';
  return p;
}

export function shopifyPlanUsesLifetimeCaptureLimit(planId) {
  return shopifyPlanById(planId).lifetimeCaptureLimit === true;
}

/** PLY download is included on all paid Shopify tiers. */
export function shopifyPlanAllowsCaptureDownload(planId) {
  return resolveShopifyPlanId(planId) !== 'shopify_free';
}

export function shopifyPlanById(planId) {
  const canonical = canonicalShopifyPlanId(planId);
  return SHOPIFY_PLANS.find((p) => p.id === canonical) ?? SHOPIFY_PLANS[0];
}

/** Max source video length for a Shopify billing tier (seconds). */
export function shopifyPlanMaxVideoDurationSec(planId) {
  const id = canonicalShopifyPlanId(planId);
  if (id === 'shopify_free') return SHOPIFY_FREE_MAX_VIDEO_DURATION_SEC;
  if (id === 'shopify_professional' || id === 'shopify_starter') {
    return SHOPIFY_PROFESSIONAL_MAX_VIDEO_DURATION_SEC;
  }
  return SHOPIFY_STUDIO_MAX_VIDEO_DURATION_SEC;
}

export function shopifyPlanMonthlyLimit(planId) {
  const plan = shopifyPlanById(planId);
  return plan.monthlyCaptureLimit;
}

export function shopifyPlanCapabilities(planId) {
  return shopifyPlanById(planId).capabilities;
}

export function formatShopifyPlanPrice(plan) {
  if (!plan || plan.priceUsd === 0) return '$0';
  return `$${plan.priceUsd}${plan.priceSuffix || ''}`;
}

export function formatShopifyPlanSummary(plan) {
  if (!plan || plan.priceUsd === 0) return plan?.title || 'Free';
  const cap = plan.monthlyCaptureLimit;
  const capLabel = plan.lifetimeCaptureLimit ? `${cap} trial captures` : `${cap} captures/mo`;
  return `${plan.title} (${formatShopifyPlanPrice(plan)}, ${capLabel})`;
}
